import React, { useState, useEffect, useRef, useMemo } from "react";
import BlogCard from "./BlogCard";

const BlogsSection = ({
  id,
  blogs = [],
  readButtonSvg,
  readButtonHoverSvg,
  circleDecorationSvg,
  title = "Latest Blog Posts"
}) => {
  // Sort blogs by publish date (most recent first)
  const sortedBlogs = useMemo(() => {
    return [...blogs].sort((a, b) => {
      return new Date(b.publishedAt) - new Date(a.publishedAt);
    }).slice(0, 9);
  }, [blogs]);
  
  // Handle opening blog in new tab
  const handleReadNow = (url) => {
    window.open(url, '_blank');
  };
  
  // State to track number of columns based on screen size
  const [columnCount, setColumnCount] = useState(3);
  // State to store our masonry layout structure
  const [masonryColumns, setMasonryColumns] = useState([]);
  // State to track if layout needs to be recalculated
  const [layoutNeedsUpdate, setLayoutNeedsUpdate] = useState(false);
  
  // Refs for container and to detect when cards are rendered
  const containerRef = useRef(null);
  const cardsRef = useRef({});
  
  // Calculate how many columns to show based on screen width
  useEffect(() => {
    const updateColumnCount = () => {
      const width = window.innerWidth;
      if (width >= 1100) {
        setColumnCount(3);
      } else if (width >= 700) {
        setColumnCount(2);
      } else {
        setColumnCount(1);
      }
    };
    
    updateColumnCount();
    window.addEventListener('resize', updateColumnCount);
    
    return () => {
      window.removeEventListener('resize', updateColumnCount);
    };
  }, []);
  
  // Initial distribution of blogs to columns
  useEffect(() => {
    if (sortedBlogs.length === 0 || columnCount === 0) return;
    
    // Initialize columns with empty arrays
    const columns = Array.from({ length: columnCount }, () => []);
    // Initialize column heights with zeros
    const columnHeights = Array(columnCount).fill(0);
    
    // Simple distribution - we'll optimize later after measuring
    sortedBlogs.forEach((blog, index) => {
      // Simple round-robin distribution for initial layout
      const columnIndex = index % columnCount;
      columns[columnIndex].push(blog);
      columnHeights[columnIndex] += 300; // Initial estimate
    });
    
    setMasonryColumns(columns);
    // Set flag to indicate we need to update layout after measuring
    if (sortedBlogs.length > 0) {
      setLayoutNeedsUpdate(true);
    }
  }, [sortedBlogs, columnCount]);
  
  // Optimize layout after cards are rendered and measured
  // Optimize layout after cards are rendered and measured
useEffect(() => {
  if (!layoutNeedsUpdate || sortedBlogs.length === 0) return;
  
  // Use setTimeout to ensure DOM is fully rendered
  const measureAndOptimizeLayout = () => {
    // Only proceed if we have card refs
    if (Object.keys(cardsRef.current).length === 0) return;
    
    // Measure actual heights of all cards
    const cardHeights = {};
    Object.entries(cardsRef.current).forEach(([id, element]) => {
      if (element) {
        cardHeights[id] = element.getBoundingClientRect().height;
      }
    });
    
    // Initialize columns
    const columns = Array.from({ length: columnCount }, () => []);
    const columnHeights = Array(columnCount).fill(0);
    
    // Distribute blogs in chronological order (newest first)
    // Always placing the blog in the shortest column
    sortedBlogs.forEach(blog => {
      const id = blog.documentId || blog.id;
      const height = cardHeights[id] || 300; // Use measured height or fallback
      
      // Find the shortest column
      const shortestColumnIndex = columnHeights.indexOf(Math.min(...columnHeights));
      
      // Add blog to the shortest column
      columns[shortestColumnIndex].push(blog);
      columnHeights[shortestColumnIndex] += height;
    });
    
    // Update state with optimized layout
    setMasonryColumns(columns);
    setLayoutNeedsUpdate(false);
  };
  
  // Delay measurement to ensure rendering is complete
  const timer = setTimeout(measureAndOptimizeLayout, 300);
  return () => clearTimeout(timer);
}, [layoutNeedsUpdate, sortedBlogs, columnCount]);

  return (
    <section
      id={id}
      className="py-16 w-full"
      style={{
        background: "linear-gradient(180deg, #ACDBE5 0%, #D9C3E6 100%)",
      }}
    >
      <div 
        className="container mx-auto px-6"
        ref={containerRef}
      >
        <h2 className="text-4xl font-bold mb-12 text-center text-[#034B6A]">
          {title}
        </h2>
        
        <div className="flex justify-center w-full">
          {/* Masonry layout container */}
          <div className="flex gap-6 w-full">
            {/* Render each column */}
            {masonryColumns.map((column, columnIndex) => (
              <div 
                key={columnIndex} 
                className="flex flex-col gap-6"
                style={{ flex: `1 1 ${100 / columnCount}%` }}
              >
                {/* Render blogs in this column */}
                {column.map((blog) => (
                  <div 
                    key={blog.documentId || blog.id}
                    ref={el => {
                      cardsRef.current[blog.documentId || blog.id] = el;
                    }}
                  >
                    <BlogCard
                      blog={{
                        ...blog,
                        url: blog.url || (blog.documentId ? `/blog/${blog.documentId}` : `/blog/${blog.id}`)
                      }}
                      isFirst={columnIndex === 0 && column.indexOf(blog) === 0}
                      readButtonSvg={readButtonSvg}
                      readButtonHoverSvg={readButtonHoverSvg}
                      circleDecorationSvg={circleDecorationSvg}
                      onClick={handleReadNow}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogsSection;